import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Send, Mail, Trash2, Search, Tag as TagIcon, Users as UsersIcon, RefreshCw, Plus, X, Edit3, Save, Brain, Wand2, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import MailchimpAIStyle from "@/components/MailchimpAIStyle";
import EmailDesigner from "@/components/EmailDesigner";
import EmailAutomations from "@/components/EmailAutomations";

export default function MailPage() {
  const [templates, setTemplates] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [senders, setSenders] = useState([]);
  const [form, setForm] = useState({ name: "", subject: "", body: "" });
  const [draft, setDraft] = useState({ purpose: "", destination: "", tone: "cálido y persuasivo" });
  const [bc, setBc] = useState({ template_id: "", sender_id: "", segment_type: "all", status: "calificado" });
  const [drafting, setDrafting] = useState(false);

  // Mailchimp audience explorer
  const [mcAudiences, setMcAudiences] = useState([]);
  const [mcSelectedAudience, setMcSelectedAudience] = useState("");
  const [mcTags, setMcTags] = useState([]);
  const [mcSelectedTag, setMcSelectedTag] = useState("");
  const [mcMembers, setMcMembers] = useState({ total_items: 0, members: [] });
  const [mcSearch, setMcSearch] = useState("");
  const [mcLoadingTags, setMcLoadingTags] = useState(false);
  const [mcLoadingMembers, setMcLoadingMembers] = useState(false);
  const [mcSortBy, setMcSortBy] = useState({ key: "last_changed", dir: "desc" });
  const [mcNlQuery, setMcNlQuery] = useState("");
  const [mcNlLoading, setMcNlLoading] = useState(false);
  const [mcNlExplanation, setMcNlExplanation] = useState("");
  const [mcSchema, setMcSchema] = useState([]);  // merge-fields schema
  const [mcEditMember, setMcEditMember] = useState(null);  // member currently being edited
  const [mcEditForm, setMcEditForm] = useState(null);
  const [mcEditSaving, setMcEditSaving] = useState(false);

  const load = () => {
    api.get("/mail/templates").then((r) => setTemplates(r.data));
    api.get("/mail/broadcasts").then((r) => setBroadcasts(r.data));
    api.get("/settings/senders").then((r) => {
      setSenders(r.data);
      const def = r.data.find((s) => s.is_default);
      if (def) setBc((p) => ({ ...p, sender_id: p.sender_id || def.id }));
    });
    loadMcAudiences();
  };
  useEffect(() => { load(); }, []);

  // ---- Mailchimp audience explorer ----
  const loadMcAudiences = () => {
    api.get("/mailchimp/audiences")
      .then((r) => setMcAudiences(r.data || []))
      .catch(() => {});
  };

  const selectAudience = async (listId) => {
    setMcSelectedAudience(listId);
    setMcSelectedTag("");
    setMcSearch("");
    setMcLoadingTags(true);
    setMcLoadingMembers(true);
    try {
      const [tagsResp, membersResp, schemaResp] = await Promise.all([
        api.get(`/mailchimp/audiences/${listId}/tags`),
        api.get(`/mailchimp/audiences/${listId}/members`, { params: { count: 50 } }),
        api.get(`/mailchimp/audiences/${listId}/merge-fields`),
      ]);
      setMcTags(tagsResp.data || []);
      setMcMembers(membersResp.data || { total_items: 0, members: [] });
      setMcSchema(schemaResp.data || []);
    } catch (e) {
      toast.error("Error cargando datos de la audiencia");
    } finally {
      setMcLoadingTags(false);
      setMcLoadingMembers(false);
    }
  };

  const selectTag = async (tagName) => {
    setMcSelectedTag(tagName);
    if (!mcSelectedAudience) return;
    setMcLoadingMembers(true);
    try {
      const params = { count: 50 };
      if (tagName) params.tag = tagName;
      if (mcSearch) params.search = mcSearch;
      const r = await api.get(`/mailchimp/audiences/${mcSelectedAudience}/members`, { params });
      setMcMembers(r.data || { total_items: 0, members: [] });
    } catch {
      toast.error("Error filtrando contactos");
    } finally {
      setMcLoadingMembers(false);
    }
  };

  const loadMcMembers = async () => {
    if (!mcSelectedAudience) return;
    setMcLoadingMembers(true);
    try {
      const params = { count: 50 };
      if (mcSelectedTag) params.tag = mcSelectedTag;
      if (mcSearch) params.search = mcSearch;
      const r = await api.get(`/mailchimp/audiences/${mcSelectedAudience}/members`, { params });
      setMcMembers(r.data || { total_items: 0, members: [] });
    } catch {
      toast.error("Error buscando");
    } finally {
      setMcLoadingMembers(false);
    }
  };

  const deleteMcMember = async (email, permanent = false) => {
    if (!mcSelectedAudience) return;
    const label = permanent ? "borrar PERMANENTEMENTE" : "archivar";
    if (!window.confirm(`¿${label} el contacto ${email}? ${permanent ? "Esto no se puede deshacer." : "Quedará archivado (no se borra del todo)."}`)) return;
    try {
      await api.delete(`/mailchimp/audiences/${mcSelectedAudience}/members/${encodeURIComponent(email)}`, {
        params: { permanent },
      });
      toast.success(`Contacto ${permanent ? "borrado" : "archivado"}`);
      loadMcMembers();
      // Refresh audience counts
      loadMcAudiences();
    } catch {
      toast.error("No se pudo borrar");
    }
  };

  const addTagToMember = async (email, currentTags) => {
    const newTag = window.prompt("Nombre del tag a añadir:");
    if (!newTag) return;
    try {
      await api.post(`/mailchimp/audiences/${mcSelectedAudience}/members/${encodeURIComponent(email)}/tags`, {
        add: [newTag.trim()],
      });
      toast.success(`Tag "${newTag.trim()}" añadido`);
      loadMcMembers();
    } catch {
      toast.error("No se pudo añadir tag");
    }
  };

  const removeTagFromMember = async (email, tag) => {
    if (!window.confirm(`¿Quitar el tag "${tag}" de ${email}?`)) return;
    try {
      await api.post(`/mailchimp/audiences/${mcSelectedAudience}/members/${encodeURIComponent(email)}/tags`, {
        remove: [tag],
      });
      toast.success(`Tag quitado`);
      loadMcMembers();
    } catch {
      toast.error("No se pudo quitar tag");
    }
  };

  const runNlQuery = async () => {
    if (!mcSelectedAudience || !mcNlQuery.trim()) return;
    setMcNlLoading(true);
    setMcNlExplanation("");
    try {
      const r = await api.post(`/mailchimp/audiences/${mcSelectedAudience}/nl-query`, {
        query: mcNlQuery,
        cap: 5000,
      });
      setMcMembers({ total_items: r.data.total_matched, members: r.data.members || [] });
      setMcNlExplanation(`${r.data.explanation} — ${r.data.total_matched} de ${r.data.total_scanned} escaneados`);
      setMcSelectedTag(""); // Clear tag filter while NL active
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error procesando la consulta");
    } finally {
      setMcNlLoading(false);
    }
  };

  const clearNlQuery = () => {
    setMcNlQuery("");
    setMcNlExplanation("");
    loadMcMembers();
  };

  const beginEditMember = (m) => {
    setMcEditMember(m);
    setMcEditForm({
      email: m.email,
      merge_fields: { ...(m.merge_fields || {}) },
      tags: [...(m.tags || [])],
    });
  };

  const cancelEditMember = () => {
    setMcEditMember(null);
    setMcEditForm(null);
  };

  const saveEditMember = async () => {
    if (!mcEditMember || !mcEditForm) return;
    setMcEditSaving(true);
    try {
      const originalTags = mcEditMember.tags || [];
      const newTags = mcEditForm.tags || [];
      const add_tags = newTags.filter((t) => !originalTags.includes(t));
      const remove_tags = originalTags.filter((t) => !newTags.includes(t));

      await api.patch(`/mailchimp/audiences/${mcSelectedAudience}/members/${encodeURIComponent(mcEditForm.email)}`, {
        merge_fields: mcEditForm.merge_fields,
        add_tags: add_tags.length ? add_tags : undefined,
        remove_tags: remove_tags.length ? remove_tags : undefined,
      });
      toast.success("Contacto actualizado en Mailchimp");
      cancelEditMember();
      loadMcMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setMcEditSaving(false);
    }
  };

  const sortedMembers = (() => {
    const arr = [...(mcMembers.members || [])];
    const { key, dir } = mcSortBy;
    arr.sort((a, b) => {
      let av = a[key] ?? "";
      let bv = b[key] ?? "";
      if (key === "tags") { av = (a.tags || []).join(","); bv = (b.tags || []).join(","); }
      if (key === "last_changed") { av = a.last_changed || ""; bv = b.last_changed || ""; }
      const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base", numeric: true });
      return dir === "asc" ? cmp : -cmp;
    });
    return arr;
  })();

  const toggleSort = (key) => {
    setMcSortBy((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  };

  const generateDraft = async () => {
    if (!draft.purpose) return toast.error("Indica el objetivo del email");
    setDrafting(true);
    try {
      const r = await api.post("/mail/draft", draft);
      setForm({ name: form.name || draft.purpose, subject: r.data.subject, body: r.data.body });
      toast.success("Borrador generado");
    } catch { toast.error("Error generando borrador"); }
    finally { setDrafting(false); }
  };

  const saveTemplate = async () => {
    if (!form.name || !form.subject || !form.body) return toast.error("Completa todos los campos");
    await api.post("/mail/templates", form);
    toast.success("Plantilla guardada");
    setForm({ name: "", subject: "", body: "" });
    load();
  };

  const removeTemplate = async (id) => {
    await api.delete(`/mail/templates/${id}`);
    load();
  };

  const sendBroadcast = async () => {
    if (!bc.template_id) return toast.error("Selecciona plantilla");
    if (!bc.sender_id) return toast.error("Selecciona remitente (configúralo en Configuración)");
    const segment = bc.segment_type === "all" ? { all: true } : { status: bc.status };
    try {
      const r = await api.post("/mail/broadcasts", { template_id: bc.template_id, sender_id: bc.sender_id, segment });
      toast.success(`Broadcast enviado a ${r.data.recipients} destinatario(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar broadcast");
    }
  };

  return (
    <div className="stagger">
      <PageHeader
        overline="Email Marketing"
        title="Reactivar y fidelizar."
        subtitle="Crea plantillas con IA y lanza campañas segmentadas a tu base de datos de leads para remarketing y mailing."
      />

      <Tabs defaultValue="designer" className="space-y-6">
        <TabsList className="bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm">
          <TabsTrigger value="designer" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="mail-tab-designer"><Wand2 className="w-3.5 h-3.5 mr-1.5" />AI Copywriter</TabsTrigger>
          <TabsTrigger value="aistyle" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="mail-tab-aistyle"><Brain className="w-3.5 h-3.5 mr-1.5" />Estilo IA</TabsTrigger>
          <TabsTrigger value="audiences" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="mail-tab-audiences">Audiencias Mailchimp</TabsTrigger>
          <TabsTrigger value="automations" className="rounded-sm data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="mail-tab-automations"><Calendar className="w-3.5 h-3.5 mr-1.5" />Automatizaciones</TabsTrigger>
        </TabsList>

        <TabsContent value="designer" className="space-y-6">
          <EmailDesigner />
        </TabsContent>

        <TabsContent value="aistyle" className="space-y-6">
          <MailchimpAIStyle />
        </TabsContent>

        {/* MAILCHIMP AUDIENCE EXPLORER */}
        <TabsContent value="audiences" className="space-y-6">
          <Card className="edit-card p-6 rounded-sm shadow-none">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h3 className="font-display text-2xl text-[#0A1128] mb-1">Audiencias de Mailchimp</h3>
                <p className="text-xs text-[#8A8F98]">Explora tus listas existentes, sus tags y contactos. Lectura desde Mailchimp en tiempo real.</p>
              </div>
              <Button
                onClick={() => loadMcAudiences()}
                size="sm"
                variant="outline"
                className="rounded-sm border-[#E2DCD0] text-[#5C616A] gap-2"
                data-testid="mc-refresh-audiences"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refrescar
              </Button>
            </div>

            {mcAudiences.length === 0 ? (
              <p className="text-sm text-[#8A8F98]">Cargando audiencias...</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {mcAudiences.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => selectAudience(a.id)}
                    data-testid={`mc-audience-${a.id}`}
                    className={`text-left p-4 rounded-sm border transition-all ${
                      mcSelectedAudience === a.id
                        ? "border-[#0A1128] bg-[#0A1128] text-white shadow-md"
                        : "border-[#E2DCD0] bg-white text-[#0A1128] hover:border-[#0A1128]"
                    }`}
                  >
                    <UsersIcon className={`w-4 h-4 mb-2 ${mcSelectedAudience === a.id ? "text-[#D95D39]" : "text-[#8A8F98]"}`} />
                    <p className="font-display text-lg leading-tight mb-1">{a.name}</p>
                    <p className={`text-[11px] uppercase tracking-wider ${mcSelectedAudience === a.id ? "text-white/70" : "text-[#8A8F98]"}`}>
                      {a.member_count.toLocaleString()} contactos
                    </p>
                  </button>
                ))}
              </div>
            )}
          </Card>

          {mcSelectedAudience && (
            <Card className="edit-card p-6 rounded-sm shadow-none">
              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="w-4 h-4 text-[#D95D39]" />
                <h4 className="font-display text-lg text-[#0A1128]">Tags / Segmentos</h4>
                {mcLoadingTags && <span className="text-xs text-[#8A8F98]">cargando...</span>}
              </div>
              <p className="text-xs text-[#8A8F98] mb-4">Click en un tag para filtrar contactos. {mcTags.length} tags encontrados.</p>
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  onClick={() => selectTag("")}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    mcSelectedTag === ""
                      ? "bg-[#0A1128] text-white border-[#0A1128]"
                      : "bg-white text-[#0A1128] border-[#E2DCD0] hover:border-[#0A1128]"
                  }`}
                  data-testid="mc-tag-all"
                >
                  Todos los contactos
                </button>
                {mcTags.map((t) => (
                  <button
                    key={t.id || t.name}
                    onClick={() => selectTag(t.name)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${
                      mcSelectedTag === t.name
                        ? "bg-[#D95D39] text-white border-[#D95D39]"
                        : "bg-white text-[#0A1128] border-[#E2DCD0] hover:border-[#D95D39] hover:text-[#D95D39]"
                    }`}
                    data-testid={`mc-tag-${(t.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span>{t.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${mcSelectedTag === t.name ? "bg-white/25" : "bg-[#F2EFE6]"}`}>
                      {t.member_count}
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {mcSelectedAudience && (
            <Card className="edit-card p-6 rounded-sm shadow-none">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#D95D39]" />
                  <h4 className="font-display text-lg text-[#0A1128]">Pregunta a Claude</h4>
                </div>
                <p className="text-xs text-[#8A8F98] mb-3">Ejemplo: "los que han viajado a Italia", "cumpleaños en febrero", "edad mayor de 50", "mujeres que han viajado a Marruecos"</p>
                <div className="flex gap-2">
                  <Input
                    value={mcNlQuery}
                    onChange={(e) => setMcNlQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !mcNlLoading && runNlQuery()}
                    placeholder="Escribe tu consulta en lenguaje natural..."
                    className="rounded-sm border-[#E2DCD0] focus:border-[#D95D39]"
                    disabled={mcNlLoading}
                    data-testid="mc-nl-input"
                  />
                  <Button
                    onClick={runNlQuery}
                    disabled={mcNlLoading || !mcNlQuery.trim()}
                    className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm shrink-0"
                    data-testid="mc-nl-submit"
                  >
                    {mcNlLoading ? "Pensando..." : "Buscar"}
                  </Button>
                  {mcNlExplanation && (
                    <Button onClick={clearNlQuery} variant="outline" className="border-[#E2DCD0] rounded-sm shrink-0">
                      Limpiar
                    </Button>
                  )}
                </div>
                {mcNlExplanation && (
                  <div className="mt-3 px-3 py-2 bg-[#FEF6E5] border-l-2 border-[#D95D39] rounded-sm text-xs text-[#0A1128]">
                    <strong>Resultado:</strong> {mcNlExplanation}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 mb-4 flex-wrap pt-4 border-t border-[#F0EBE2]">
                <h4 className="font-display text-lg text-[#0A1128]">
                  Contactos
                  <span className="text-sm text-[#8A8F98] ml-2 font-sans">
                    ({mcMembers.total_items.toLocaleString()} {mcSelectedTag && `con tag "${mcSelectedTag}"`})
                  </span>
                </h4>
                <div className="relative">
                  <Search className="w-4 h-4 text-[#8A8F98] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    placeholder="Buscar por email o nombre..."
                    value={mcSearch}
                    onChange={(e) => setMcSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadMcMembers()}
                    className="pl-9 w-72 rounded-sm border-[#E2DCD0]"
                    data-testid="mc-members-search"
                  />
                </div>
              </div>
              {mcLoadingMembers ? (
                <p className="text-sm text-[#8A8F98] py-10 text-center">Cargando contactos...</p>
              ) : mcMembers.members.length === 0 ? (
                <p className="text-sm text-[#8A8F98] py-10 text-center">Sin contactos con estos filtros.</p>
              ) : (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E2DCD0] text-[10px] uppercase tracking-wider text-[#8A8F98]">
                        <th onClick={() => toggleSort("name")} className="text-left py-2 px-2 cursor-pointer hover:text-[#D95D39] select-none">Nombre {mcSortBy.key === "name" ? (mcSortBy.dir === "asc" ? "↑" : "↓") : ""}</th>
                        <th onClick={() => toggleSort("email")} className="text-left py-2 px-2 cursor-pointer hover:text-[#D95D39] select-none">Email {mcSortBy.key === "email" ? (mcSortBy.dir === "asc" ? "↑" : "↓") : ""}</th>
                        <th onClick={() => toggleSort("status")} className="text-left py-2 px-2 cursor-pointer hover:text-[#D95D39] select-none">Status {mcSortBy.key === "status" ? (mcSortBy.dir === "asc" ? "↑" : "↓") : ""}</th>
                        <th onClick={() => toggleSort("tags")} className="text-left py-2 px-2 cursor-pointer hover:text-[#D95D39] select-none">Tags {mcSortBy.key === "tags" ? (mcSortBy.dir === "asc" ? "↑" : "↓") : ""}</th>
                        <th onClick={() => toggleSort("last_changed")} className="text-left py-2 px-2 cursor-pointer hover:text-[#D95D39] select-none">Última act. {mcSortBy.key === "last_changed" ? (mcSortBy.dir === "asc" ? "↑" : "↓") : ""}</th>
                        <th className="text-right py-2 px-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMembers.map((m) => (
                        <tr key={m.id} className="border-b border-[#F0EBE2] hover:bg-[#F9F8F6]">
                          <td className="py-2 px-2 text-[#0A1128] font-medium">{m.name}</td>
                          <td className="py-2 px-2 text-[#5C616A]">{m.email}</td>
                          <td className="py-2 px-2">
                            <Badge variant="outline" className={`rounded-sm text-[10px] ${
                              m.status === "subscribed" ? "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5" :
                              m.status === "unsubscribed" ? "border-[#9D0208] text-[#9D0208] bg-[#9D0208]/5" :
                              "border-[#E2DCD0] text-[#8A8F98]"
                            }`}>
                              {m.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex flex-wrap gap-1 items-center">
                              {(m.tags || []).slice(0, 4).map((tg) => (
                                <span
                                  key={tg}
                                  className="group text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F2EFE6] text-[#5C616A] inline-flex items-center gap-1 cursor-pointer hover:bg-[#E2DCD0]"
                                  onClick={() => removeTagFromMember(m.email, tg)}
                                  title="Click para quitar"
                                >
                                  {tg}
                                  <X className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
                                </span>
                              ))}
                              {(m.tags || []).length > 4 && (
                                <span className="text-[10px] text-[#8A8F98]">+{m.tags.length - 4}</span>
                              )}
                              <button
                                onClick={() => addTagToMember(m.email, m.tags || [])}
                                className="text-[10px] px-1.5 py-0.5 rounded-sm border border-dashed border-[#E2DCD0] text-[#8A8F98] hover:border-[#D95D39] hover:text-[#D95D39] inline-flex items-center gap-1"
                                title="Añadir tag"
                              >
                                <Plus className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-[#8A8F98] text-xs">
                            {m.last_changed ? new Date(m.last_changed).toLocaleDateString() : "—"}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                onClick={() => beginEditMember(m)}
                                size="icon"
                                variant="outline"
                                className="h-7 w-7 rounded-sm border-[#E2DCD0] text-[#0A1128] hover:text-[#D95D39] hover:border-[#D95D39]"
                                title="Editar contacto"
                                data-testid={`mc-edit-${m.email}`}
                              >
                                <Edit3 className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => deleteMcMember(m.email, false)}
                                size="icon"
                                variant="outline"
                                className="h-7 w-7 rounded-sm border-[#E2DCD0] text-[#8A8F98] hover:text-[#0A1128]"
                                title="Archivar (recuperable)"
                                data-testid={`mc-archive-${m.email}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => deleteMcMember(m.email, true)}
                                size="icon"
                                variant="outline"
                                className="h-7 w-7 rounded-sm border-[#E2DCD0] text-[#8A8F98] hover:text-[#9D0208] hover:border-[#9D0208]"
                                title="Borrar permanentemente"
                                data-testid={`mc-delete-${m.email}`}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="automations" className="space-y-6">
          <EmailAutomations />
        </TabsContent>
      </Tabs>

      {/* MAILCHIMP CONTACT EDIT DIALOG */}
      <Dialog open={!!mcEditMember} onOpenChange={(open) => !open && cancelEditMember()}>
        <DialogContent className="bg-white rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          {mcEditMember && mcEditForm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-[#0A1128]">
                  Editar contacto · {mcEditMember.name}
                </DialogTitle>
                <p className="text-xs text-[#8A8F98] mt-1">
                  Los cambios se guardan en Mailchimp y se reflejan en el CRM si existe el lead.
                </p>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                <div>
                  <Label className="text-xs">Email <span className="text-[#8A8F98]">(no editable)</span></Label>
                  <Input value={mcEditForm.email} disabled className="mt-1 rounded-sm bg-[#F9F8F6]" />
                </div>

                {/* Tags */}
                <div>
                  <Label className="text-xs">Tags</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5 p-3 border border-[#E2DCD0] rounded-sm bg-white min-h-[44px]">
                    {(mcEditForm.tags || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#F2EFE6] text-xs text-[#0A1128]">
                        {t}
                        <button
                          onClick={() => setMcEditForm({ ...mcEditForm, tags: mcEditForm.tags.filter((x) => x !== t) })}
                          className="hover:text-[#9D0208]"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder="Añadir tag + Enter..."
                      className="flex-1 min-w-[120px] text-xs bg-transparent outline-none placeholder:text-[#8A8F98]"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && e.currentTarget.value.trim()) {
                          e.preventDefault();
                          const v = e.currentTarget.value.trim();
                          if (!mcEditForm.tags.includes(v)) {
                            setMcEditForm({ ...mcEditForm, tags: [...mcEditForm.tags, v] });
                          }
                          e.currentTarget.value = "";
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Merge fields */}
                {mcSchema.length > 0 && (
                  <div className="pt-3 border-t border-[#F0EBE2]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-3">
                      Datos del contacto ({mcSchema.length} campos)
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {mcSchema.map((f) => {
                        const v = (mcEditForm.merge_fields || {})[f.tag];
                        const setVal = (val) => setMcEditForm({
                          ...mcEditForm,
                          merge_fields: { ...(mcEditForm.merge_fields || {}), [f.tag]: val },
                        });
                        const type = f.type === "number" ? "number" : (f.type === "date" ? "date" : "text");
                        const placeholder = f.type === "birthday" ? "MM/DD (ej. 06/20)" : "";
                        return (
                          <div key={f.tag}>
                            <Label className="text-xs">{f.name} <span className="text-[#8A8F98]">({f.tag} · {f.type})</span></Label>
                            <Input
                              type={type}
                              placeholder={placeholder}
                              value={typeof v === "object" ? "" : (v ?? "")}
                              onChange={(e) => setVal(e.target.value)}
                              className="mt-1 rounded-sm text-sm"
                              data-testid={`mc-edit-field-${f.tag}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="pt-4 border-t border-[#E2DCD0]">
                <Button onClick={cancelEditMember} variant="outline" className="rounded-sm border-[#E2DCD0]">
                  Cancelar
                </Button>
                <Button
                  onClick={saveEditMember}
                  disabled={mcEditSaving}
                  className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm"
                  data-testid="mc-edit-save"
                >
                  {mcEditSaving ? "Guardando..." : (<><Save className="w-3.5 h-3.5 mr-1.5" />Guardar</>)}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
